import React, { useEffect, useState } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactPagination from 'react-js-pagination';
import { faCheck, faTimes, faFileAlt } from '@fortawesome/pro-duotone-svg-icons';

// Actions
import { loadSOAs } from '../../../../../actions/soa';

// Components
import Table from '../../../../../common/components/Table';
// import TableFilter from './TableFilter';

// Utilities
import { withRouterProps } from '../../../../../common/hocs';
import { prettifyPhone } from '../../../../../common/utils/prettify';

function SOAsTable() {
  const dispatch = useDispatch();
  const {
    user: { username: profileUsername },
    auth: { token },
    agents: {
      selectedAgent: { username: agentUsername } = {},
    },
    shoppers: {
      selectedShopper: { username: shopperUsername } = {},
    },
    theme: { soaLink = '' },
    soas: {
      soas = [],
      sortInverse,
      sortTerm,
      page,
      pageSize,
      totalItems = 0,
    },
  } = useSelector(store => store);

  const [ error, setError ] = useState('');
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    dispatch({ type: 'CLEAR_SOAS' });
    setLoading(true);
    loadSOAs({
      agentUsername: agentUsername || profileUsername,
      shopperUsername,
    })
      .then((data) => dispatch({ type: 'LOAD_SOAS', payload: data }))
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [sortTerm, sortInverse]);

  /* eslint-disable no-param-reassign */
  function handleSort(newSortTerm) {
    let newSortInverse = sortInverse;

    // Conditional order important for [ down -> up -> off ] w/ changing sorts
    const setInitialSort = sortTerm === null && newSortTerm !== sortTerm;
    const setNewSort = newSortTerm !== sortTerm;
    const setInverseSort = sortTerm !== null && sortInverse === false;
    const setDefaultSort = sortTerm !== null && sortInverse === true;
    if (setInitialSort || setNewSort) {
      newSortInverse = false;
    } else if (setInverseSort) {
      newSortInverse = !sortInverse;
    } else if (setDefaultSort) {
      newSortTerm = null;
      newSortInverse = false;
    }

    return new Promise((resolve) => {
      dispatch({ type: 'SET_SOAS_PAGE', payload: 1 });
      dispatch({ type: 'SET_SOAS_SORT_INVERSE', payload: newSortInverse });
      dispatch({ type: 'SET_SOAS_SORT_TERM', payload: newSortTerm });
      resolve();
    });
  }

  function handlePaginate(pageNumber) {
    dispatch({ type: 'SET_SOAS_PAGE', payload: pageNumber });
    loadSOAs({
      agentUsername: agentUsername || profileUsername,
      shopperUsername,
    })
      .then((data) => dispatch({ type: 'LOAD_SOAS', payload: data }));
  }

  function handleClick(traceID) {
    // NOTE(joeysapp): manage-portal's auth token is in the store.
    // const token = localStorage.getItem('SessionToken');
    const site = process.env.REACT_APP_HOSTNAME || window.location.hostname;
    const cookieAge = 60 * 1; // One minute expiration
    const cookieDomain = '.mymedicarebot.com';

    document.cookie = `soaToken=${token}; max-age=${cookieAge}; domain=${cookieDomain}; path=/; Secure;`;
    document.cookie = `site=${site}; max-age=${cookieAge}; domain=${cookieDomain}; path=/; Secure;`;

    const url = `${soaLink}/submitted/${traceID}`;
    window.open(url);
  }


  const formatDateTimeCell = (date) => (date ? moment(date).format('MM/DD/YYYY hh:mm A') : '--');
  const formatBooleanCell = (active) => (
    <span className="d-flex justify-content-center">
      {active ? (
        <FontAwesomeIcon className="text-success" icon={faCheck} />
      ) : (
        <FontAwesomeIcon className="text-danger" icon={faTimes} />
      )}
    </span>
  );

  const formatViewCell = ({ trace_id, submit_date, other }) => (submit_date && !other ? (
    <span
      className="link-secondary me-2 text-decoration-underline"
      style={{ cursor: 'pointer' }}
      onClick={() => handleClick(trace_id)}
      role="button"
      tabIndex={0}
    >
      <FontAwesomeIcon className="me-2" icon={faFileAlt} />
      View
    </span>
  ) : (<i className="text-muted">External</i>));

  const tableSchema = {
    cols: [
      // Indicates whether agent or shopper agreement
      // {
      //   header: 'Agreement',
      //   field: 'agree',
      //   sortKey: 'agree',
      //   formatter: formatBooleanCell,
      // },
      // Indicates if submitted through MMB by 'representative' checkbox
      // {
      //   header: 'Representative',
      //   field: 'representative',
      //   sortKey: 'representative',
      //   formatter: formatBooleanCell,
      // },
      /*
      {
        header: 'First name',
        field: 'first_name',
        sortKey: 'first_name',
      },
      {
        header: 'Last name',
        field: 'last_name',
        sortKey: 'last_name',
      },
      {
        header: 'Submitted name',
        field: 'name',
        sortKey: 'name',
      },
      {
        header: 'E. Signature',
        field: 'e_signature',
        sortKey: 'e_signature',
      },
      */
      {
        header: 'Phone',
        field: 'phone',
        formatter: prettifyPhone,
      },
      {
        header: 'Email',
        field: 'email',
      },
      {
        header: 'Delivery method',
        field: 'delivery_method',
      },

      {
        header: 'Submitted',
        field: 'none',
        formatter: (data = {}) => formatBooleanCell(data.submit_date),
        passAllData: true,
      },
      {
        header: 'Submission Date',
        field: 'submit_date',
        formatter: formatDateTimeCell,
        sortKey: 'submit_date',
      },
      {
        header: 'Created',
        field: 'created',
        formatter: formatDateTimeCell,
        sortKey: 'created',
      },
      {
        header: 'Opened Date',
        field: 'opened_date',
        formatter: formatDateTimeCell,
        sortKey: 'opened_date',
      },

      {
        header: 'Details',
        field: 'trace_id',
        passAllData: true,
        formatter: (data) => formatViewCell(data),
      },
      {
        header: 'MAPD/MA',
        field: 'mapd_ma',
        sortKey: 'mapd_ma',
        formatter: formatBooleanCell,
      },
      {
        header: 'PDP',
        field: 'pdp',
        sortKey: 'pdp',
        formatter: formatBooleanCell,
      },
      {
        header: 'Supp. health products',
        field: 'sup_health',
        sortKey: 'sup_health',
        formatter: formatBooleanCell,
      },
      {
        header: 'Dental / vision / hearing',
        field: 'dvh',
        sortKey: 'dvh',
        formatter: formatBooleanCell,
      },
      {
        header: 'Medicare supp.',
        field: 'medigap',
        sortKey: 'medigap',
        formatter: formatBooleanCell,
      },
      /*
      {
        header: 'Lead ID',
        field: 'lead_id',
        formatter: (data) => data || '--',
      },
      */
      {
        header: 'SOA ID',
        field: 'trace_id',
        sortKey: 'trace_id',
        // formatter: (data) => <span className='font-monospace'>{data}</span>,
      },

    ],
    subComponentContainerStyle: { backgroundColor: 'rgba(var(--bs-primary-rgb), 0.13)' },

    sortKeySetter: (sk) => dispatch({ type: 'SET_SOAS_SORT_TERM', payload: sk }),
    sortKeyGetter: () => sortTerm,
    sortDirectionSetter: (sd) => dispatch({ type: 'SET_SOAS_SORT_INVERSE', payload: (sd === 'asc') }),
    sortDirectionGetter: () => ((sortInverse === true) ? 'asc' : 'desc'),
    sortClickCallback: () => handlePaginate(1),
  };

  return (
    <div className="bg-white border">
      <div className="text-nowrap overflow-scroll">
        {/* <TableFilter /> */}
        <Table
          schema={tableSchema}
          data={soas}
          sort={sortTerm}
          desc={sortInverse}
          onClickTh={(id) => {
            handleSort(id).then(() => null /* handleSearch() */);
          }}
          error={error}
          className="table table-striped mb-0"
          loading={loading}
        />
      </div>
      <div className={`${totalItems > 0 ? 'd-flex' : 'd-none'} m-3 position-relative`}>
        <div className="d-flex ms-0 position-absolute">
          <span className="text-muted">
            <i>{`${1 + pageSize * (page - 1)}-${Math.min((pageSize * page), totalItems)} of ${totalItems} soas`}</i>
          </span>
        </div>
        <ReactPagination
          activePage={page}
          itemsCountPerPage={pageSize}
          totalItemsCount={totalItems}
          pageRangeDisplayed={5}
          innerClass="pagination mb-0 ms-auto me-auto"
          itemClass="page-item"
          linkClass="page-link"
          onChange={(pageNum) => handlePaginate(pageNum)}
        />
      </div>
    </div>
  );
}

export default connect()(withRouterProps(SOAsTable));
